import { useQuery } from "@/lib/query";
import { useNavigate } from "react-router";
import { Loader2 } from "lucide-react";
import { useEffect, type ReactNode } from "react";

import { AppShell } from "@/components/layout/app-shell";
import { PendingReview } from "@/components/doctor/pending-review";
import { ApiError } from "@/lib/api/client";
import { meQuery } from "@/lib/api/doctorApi";
import { useAuth } from "@/contexts/AuthContext";
import type { Doctor } from "@/types/doctor";

function FullPageLoader({ label }: { label: string }) {
  return (
    <div className="grid min-h-screen place-items-center bg-background">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Loader2 className="size-6 animate-spin text-primary" />
        <p className="text-sm">{label}</p>
      </div>
    </div>
  );
}

/**
 * Gate for every portal page: requires a signed-in clinician with a doctor
 * profile, and renders the review screen while that profile is unapproved.
 */
export function RequireDoctor({
  children,
  allowUnapproved = false,
}: {
  children: (doctor: Doctor) => ReactNode;
  allowUnapproved?: boolean | undefined;
}) {
  const { uid, loading } = useAuth();
  const navigate = useNavigate();
  const query = useQuery(meQuery(uid));

  useEffect(() => {
    if (!loading && !uid) navigate("/login", { replace: true });
  }, [loading, uid, navigate]);

  // No doctor profile yet — the clinician still has to register.
  // The backend's doctorAuthMiddleware returns 403 when no Doctor row exists for the UID.
  const notRegistered =
    query.error instanceof ApiError && (query.error.status === 404 || query.error.status === 403);
  useEffect(() => {
    if (notRegistered) navigate("/register", { replace: true });
  }, [notRegistered, navigate]);

  if (loading || !uid) return <FullPageLoader label="Checking your session…" />;
  if (query.isPending) return <FullPageLoader label="Loading your practice…" />;

  if (query.error) {
    if (notRegistered) return <FullPageLoader label="Redirecting to registration…" />;
    const message =
      query.error instanceof ApiError
        ? query.error.message
        : "We couldn't reach HealNova just now.";
    return (
      <div className="grid min-h-screen place-items-center bg-background px-4">
        <div className="card-surface max-w-md p-8 text-center">
          <h1 className="font-display text-xl font-semibold">We couldn't load your profile</h1>
          <p className="mt-2 text-sm text-muted-foreground">{message}</p>
        </div>
      </div>
    );
  }

  const doctor = query.data;
  // No error and no data means the request settled without a body — treat it
  // as still loading rather than rendering the shell with nothing in it.
  if (!doctor) return <FullPageLoader label="Loading your practice…" />;

  if (!allowUnapproved && doctor.status !== "approved") {
    return <PendingReview doctor={doctor} />;
  }

  return <AppShell doctor={doctor}>{children(doctor)}</AppShell>;
}
